import { Button, CollapsibleDiv, IconButton, Tooltip, Typography } from '@recruit-robin/robin-theme/web-components';
import {
  Eye,
  LinkExternal_02,
  Mail_01,
  RefreshCw_02,
  Reply,
  RobinIconMonoFulfilled,
  Trash_01,
  Whatsapp,
  XCircle,
} from '@recruit-robin/robin-theme/web-icons';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { BulkSlot } from '@/features/generate-ai-reachout-message';
import {
  GenerateAiReachoutMessageEditor,
  type GenerateAiReachoutMessageEditorRef,
} from '@/features/generate-ai-reachout-message';
import { CandidatePreviewModal } from '@/features/reachout-message-preview';
import { useDictionary } from '@/shared/lib/i18n';
import { WhatsAppPreview } from '@/shared/ui/whatsapp-preview';
import { Indeed } from 'shared/legacy/icons/Indeed/Indeed';

import { useStyles } from './BulkGenerationProgress.styles';

type BulkCandidateRowProps = {
  slot: BulkSlot;
  jobImprovedId: number;
  tokens: Record<string, string>;
  showSubject: boolean;
  isWhatsapp: boolean;
  isIndeedFlow: boolean;
  onRegisterEditorRef: (esPersonId: string, ref: GenerateAiReachoutMessageEditorRef | null) => void;
  onDelete: (esPersonId: string) => void;
  onRegenerate: (esPersonId: string) => Promise<void>;
};

function hasSubject(data: unknown): data is { subject: string; message: string } {
  return data != null && typeof data === 'object' && 'subject' in data;
}

export function BulkCandidateRow({
  slot,
  jobImprovedId,
  tokens,
  showSubject,
  isWhatsapp,
  isIndeedFlow,
  onRegisterEditorRef,
  onDelete,
  onRegenerate,
}: BulkCandidateRowProps) {
  const classes = useStyles();
  const dictionary = useDictionary();
  const editorRef = useRef<GenerateAiReachoutMessageEditorRef | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const isReady = slot.status === 'success';
  const isLoading = !isWhatsapp && (slot.status === 'pending' || slot.status === 'loading');
  const isError = slot.status === 'error';

  const whatsappPreviewItems = useMemo(
    () =>
      isWhatsapp && slot.data
        ? [
            { type: 'message' as const, text: slot.data.message },
            {
              type: 'button' as const,
              text: dictionary.viewVacancy,
              icon: <LinkExternal_02 color={(c) => c.secondary.sky[60]} />,
            },
            {
              type: 'button' as const,
              text: dictionary.noVacancyAnymore,
              icon: <Reply size={24} color={(c) => c.secondary.sky[60]} />,
            },
          ]
        : [],
    [isWhatsapp, slot.data, dictionary.viewVacancy, dictionary.noVacancyAnymore],
  );

  const handleEditorRef = useCallback(
    (ref: GenerateAiReachoutMessageEditorRef | null) => {
      editorRef.current = ref;
      onRegisterEditorRef(slot.esPersonId, ref);
    },
    [slot.esPersonId, onRegisterEditorRef],
  );

  const handleDeleteClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsDeleting(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    onDelete(slot.esPersonId);
    setIsDeleting(false);
  }, [slot.esPersonId, onDelete, setIsDeleting]);

  const confirmRemoveName = (slot.candidateName ?? '').split(' ')[0] ?? '';

  const showDeleteOnHover = isReady || isError;

  const titleLeadIcon = isReady ? (
    isIndeedFlow ? (
      <Indeed width={16} height={16} />
    ) : isWhatsapp ? (
      <Whatsapp size={16} />
    ) : (
      <Mail_01 size={16} color={(c) => c.neutral.variant[70]} />
    )
  ) : isError ? (
    <XCircle size={16} color={(c) => c.tertiary.red[50]} />
  ) : (
    <RobinIconMonoFulfilled size={16} color={(c) => c.primary.fuchsia[40]} />
  );

  const titleLead = showDeleteOnHover ? (
    <div css={classes.titleLeadSlot}>
      <div data-bulk-title-lead="mail" css={classes.titleLeadMail}>
        {titleLeadIcon}
      </div>
      <div data-bulk-title-lead="delete">
        <Tooltip label={dictionary.delete} zIndex={1002}>
          <IconButton
            type="delete"
            style="default"
            label={dictionary.delete}
            icon={<Trash_01 size={16} color={(c) => c.tertiary.red[50]} />}
            onClick={handleDeleteClick}
            customCss={classes.deleteButton}
          />
        </Tooltip>
      </div>
    </div>
  ) : (
    <div css={classes.iconTitleRow}>{titleLeadIcon}</div>
  );

  const title = (
    <div css={[classes.titleRow, showDeleteOnHover && classes.titleRowReady]}>
      {titleLead}
      {isLoading && (
        <Typography variant={(v) => v.heading[5]} color={(c) => c.primary.fuchsia[40]}>
          {dictionary.generatingMessage(slot.candidateName ?? '')}
        </Typography>
      )}
      {isError && (
        <Typography variant={(v) => v.heading[5]} color={(c) => c.tertiary.red[60]}>
          {dictionary.errorGeneratingMessage(slot.candidateName ?? '')}
        </Typography>
      )}
      {isReady && (
        <Typography variant={(v) => v.heading[5]} color={(c) => c.neutral.variant[80]}>
          {slot.candidateName ?? ''}
        </Typography>
      )}
    </div>
  );

  const handleRegenerateClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRegenerate(slot.esPersonId);
    },
    [slot.esPersonId, onRegenerate],
  );

  const handlePreviewClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    window.dispatchEvent(new MouseEvent('click', { clientX: e.clientX, clientY: e.clientY }));
    setIsPreviewModalOpen(true);
  }, []);

  const previewMessage = (): string => {
    const editedMessage = editorRef.current?.getContent().message;
    return editedMessage ?? slot.data?.message ?? '';
  };

  const showRegenerate = !isWhatsapp && (isReady || isError);

  const endAdornment = showRegenerate ? (
    <div css={classes.endAdornmentGroup}>
      <Tooltip label={dictionary.regenerateMessage} zIndex={3}>
        <div css={classes.endAdornment} onClick={handleRegenerateClick}>
          <RefreshCw_02 size={16} color={(c) => c.primary.fuchsia[50]} />
        </div>
      </Tooltip>
      {isReady && (
        <Tooltip label={dictionary.previewAsCandidate} zIndex={3}>
          <div css={classes.endAdornment} onClick={handlePreviewClick}>
            <Eye size={16} color={(c) => c.neutral.variant[70]} />
          </div>
        </Tooltip>
      )}
    </div>
  ) : undefined;

  if (isDeleting) {
    return (
      <div css={classes.deletingRow}>
        <div css={classes.deletingTitleRow}>
          <Trash_01 size={16} color={(c) => c.neutral.variant[70]} />
          <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
            {dictionary.confirmRemoveBulkCandidate(confirmRemoveName)}
          </Typography>
        </div>
        <div css={classes.deletingButtonsRow}>
          <Button
            label={
              <Typography variant={(v) => v.supporting['label-tiny']} color={(c) => c.neutral.variant[70]}>
                {dictionary.cancel}
              </Typography>
            }
            customCss={classes.removeButtonsRow}
            onClick={() => setIsDeleting(false)}
            style="filled"
            type="subtle2"
          />
          <Button
            label={<Typography variant={(v) => v.supporting['label-tiny']}>{dictionary.remove}</Typography>}
            onClick={handleConfirmDelete}
            customCss={[classes.removeButtonsRow, classes.removeButton]}
            style="filled"
            type="danger"
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <CollapsibleDiv
        title={title}
        endAdornment={endAdornment}
        chevronPosition="right"
        initialOpen={false}
        disabled={!isReady && !isError}
        hideChevron={!isReady && !isError}
        customHeaderCss={classes.headerRow}
        customCss={classes.collapsibleRow}>
        {isError && (
          <div css={classes.generatingContent}>
            <Typography variant={(v) => v.body.short} color={(c) => c.tertiary.red[60]}>
              {slot.error?.message ?? dictionary.errorGeneratingMessage(slot.candidateName ?? '')}
            </Typography>
          </div>
        )}
        {isReady && slot.data && isWhatsapp && (
          <div css={classes.expandedContent}>
            <WhatsAppPreview height={Math.round(window.innerHeight * 0.35)} items={whatsappPreviewItems} />
          </div>
        )}
        {isReady && slot.data && !isWhatsapp && (
          <div css={classes.expandedContent}>
            <GenerateAiReachoutMessageEditor
              ref={handleEditorRef}
              subject={hasSubject(slot.data) ? slot.data.subject : ''}
              message={slot.data.message}
              tokens={tokens}
              showSubject={showSubject}
            />
          </div>
        )}
      </CollapsibleDiv>

      {!isWhatsapp && (
        <CandidatePreviewModal
          jobImprovedId={jobImprovedId}
          isVisible={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
          message={previewMessage()}
          isIndeedFlow={isIndeedFlow}
        />
      )}
    </>
  );
}
